import { createContext, useContext, useState, useEffect } from "react";

type User = {
  id: string;
  email: string;
  name: string;
  plan: string;
  google_id: string | null;
  picture: string;
  country?: string;
  country_code?: string;
  report_count?: number;
  member_since?: string;
};

type AuthContextType = {
  user: User | null;
  picture: string;
  loading: boolean;
  logout: () => void;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  picture: "",
  loading: true,
  logout: () => {},
  refreshUser: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [picture, setPicture] = useState("");
  const [loading, setLoading] = useState(true);

  const refreshUser = async (): Promise<void> => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      setPicture("");
      setLoading(false);
      return;
    }
    try {
      const res = await fetch("/api/auth/me", {
        headers: { Authorization: "Bearer " + token },
      });
      const data = await res.json();
      if (data.email) {
        if (!user) {
          (window as any).gtag?.('event', 'login', { method: 'google' });
        }
        setUser(data as User);
        const pic: string = data.picture || localStorage.getItem("userPicture") || "";
        setPicture(pic);
        if (pic) localStorage.setItem("userPicture", pic);
      } else {
        localStorage.removeItem("token");
        setUser(null);
        setPicture("");
      }
    } catch {
      localStorage.removeItem("token");
      setUser(null);
      setPicture("");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userPicture");
    setUser(null);
    setPicture("");
    window.location.href = "/";
  };

  useEffect(() => {
    refreshUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{ user, picture, loading, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
